import { GraduationCap, MapPin, ExternalLink, Star } from 'lucide-react'
import { education } from '../data/education'
import useInView from '../lib/useInView'

export default function Education() {
  const [ref, visible] = useInView()

  return (
    <section id="education" className="section-padding bg-[#0a0a0a]">
      <div className="section-container" ref={ref}>

        <div className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="section-label">// 04. education</span>
          <h2 className="section-heading">Education</h2>
          <div className="section-divider" />
        </div>

        <div className="space-y-6">
          {education.map((edu, i) => (
            <div
              key={edu.id}
              className={`rounded-2xl border border-[#1e293b] bg-[#111111] p-8 card-hover transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 140}ms` }}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl border border-[#1e293b] bg-[#0d0d0d] flex items-center justify-center flex-shrink-0">
                    <GraduationCap size={22} className="text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#f1f5f9] mb-1">{edu.degree}</h3>
                    {edu.institutionUrl ? (
                      <a
                        href={edu.institutionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 font-medium transition-colors duration-200 mb-1"
                      >
                        {edu.institution} <ExternalLink size={11} />
                      </a>
                    ) : (
                      <p className="text-indigo-400 font-medium mb-1">{edu.institution}</p>
                    )}
                    <p className="flex items-center gap-1 text-[#94a3b8] text-sm">
                      <MapPin size={11} /> {edu.location}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                  <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 rounded-full">
                    {edu.startYear} — {edu.endYear}
                  </span>
                  {edu.gpa && (
                    <span className="flex items-center gap-1 font-mono text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-3 py-1 rounded-full">
                      <Star size={9} /> GPA: {edu.gpa}
                    </span>
                  )}
                  {edu.honors && (
                    <span className="font-mono text-xs text-green-400">{edu.honors}</span>
                  )}
                </div>
              </div>

              {edu.coursework && (
                <div className="mb-5">
                  <h4 className="font-mono text-xs text-[#94a3b8] uppercase tracking-widest mb-3">Relevant Coursework</h4>
                  <div className="flex flex-wrap gap-2">
                    {edu.coursework.map(c => (
                      <span key={c} className="px-3 py-1 rounded-full text-xs border border-[#1e293b] bg-[#0d0d0d] text-[#94a3b8] hover:border-indigo-500/40 hover:text-indigo-300 transition-colors duration-200">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {edu.activities && (
                <div>
                  <h4 className="font-mono text-xs text-[#94a3b8] uppercase tracking-widest mb-3">Activities</h4>
                  <ul className="space-y-1.5">
                    {edu.activities.map(a => (
                      <li key={a} className="flex items-center gap-2 text-[#94a3b8] text-sm">
                        <span className="font-mono text-indigo-400 text-xs">→</span>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
